<!--
 * @Description: 项目核场记录新增/编辑
-->
<template>
  <scroll-layout class="inspection-record-edit">
    <template slot="header">
      <div class="page-header">
        <el-button type="text" class="back-btn" @click="goBack">
          <i class="el-icon-arrow-left"></i>
          返回列表
        </el-button>
        <h2 class="page-title">
          {{ isEdit ? '编辑核场记录' : '新增核场记录' }}
          <span v-if="detail.recordCode" class="record-code-tag">{{ detail.recordCode }}</span>
        </h2>
        <div class="header-actions">
          <el-button size="small" @click="goBack">取消</el-button>
          <el-button
            v-if="!isSubmitted && !loading"
            size="small"
            type="warning"
            :loading="saving"
            @click="handleSaveDraft">
            保存草稿
          </el-button>
          <el-button
            v-if="!isInitiatedRecord"
            size="small"
            type="primary"
            :loading="submitting"
            @click="handleSubmit">
            <i class="el-icon-paperplane"></i>
            提交
          </el-button>
        </div>
      </div>
    </template>

    <div class="edit-body" v-loading="loading">
      <!-- 填写提示 -->
      <div class="ai-notice">
        <i class="el-icon-lightbulb"></i>
        <span>
          💡 填写提示：标注
          <span class="required">*</span>
          的编辑项均为必填；带有「AI」标识的编辑项支持智能检索填充，AI 生成内容仅供参考，
          请务必按照实际情况进行核验确认或修改
        </span>
      </div>

      <!-- 项目基础信息 -->
      <div class="section-card" :class="{ collapsed: !expandedSections.basic }">
        <div class="section-header" @click="toggleSection('basic')">
          <div class="section-header-left">
            <div class="section-icon" style="background: #eff6ff; color: #3b82f6">
              <i class="el-icon-info"></i>
            </div>
            <div>
              <h3 class="section-title">项目基础信息</h3>
              <p class="section-subtitle">项目名称、位置、规模等</p>
            </div>
          </div>
          <i
            class="el-icon-arrow-down section-arrow"
            :class="{ rotated: !expandedSections.basic }"></i>
        </div>
        <div class="section-content" v-show="expandedSections.basic">
          <div class="form-row">
            <div class="form-item form-item-full">
              <div class="form-label-row">
                <span class="form-label">
                  项目名称
                  <span class="required">*</span>
                </span>
              </div>
              <el-input
                v-model="form.projectName"
                placeholder="请输入公开对外名称"
                size="small"
                clearable
                ref="projectNameInput"
                @change="checkProjectNameDuplicate" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-item form-item-full">
              <span class="form-label">定位城市</span>
              <el-select
                :value="locationCity"
                :placeholder="locatingCity ? '正在定位...' : '进入页面自动定位，也可手动选择'"
                size="small"
                style="width: 100%"
                filterable
                clearable
                @change="onLocationCityChange">
                <el-option-group
                  v-for="group in locationCityGroups"
                  :key="group.province"
                  :label="group.province">
                  <el-option
                    v-for="item in group.cities"
                    :key="item.id"
                    :label="item.name"
                    :value="item.name"></el-option>
                </el-option-group>
              </el-select>
            </div>
          </div>

          <div class="batch-upload-bar smart-retrieve-bar" @click="handleAiUpdate">
            <i class="el-icon-refresh"></i>
            <span>智能检索填写</span>
          </div>

          <div class="form-row">
            <div class="form-item form-item-full">
              <span class="form-label">
                站点名称
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.stationName"
                placeholder="请输入站点名称"
                size="small"
                clearable />
            </div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                站点性质
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-input
                v-model="form.siteNature"
                placeholder="如住宅、酒店"
                size="small"
                clearable />
            </div>
            <div class="form-item">
              <span class="form-label">
                核场时间
                <span class="required">*</span>
              </span>
              <el-date-picker
                v-model="form.surveyTime"
                type="datetime"
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="选择核场时间"
                size="small"
                style="width: 100%"></el-date-picker>
            </div>
          </div>

          <div class="form-row form-row-3">
            <div class="form-item">
              <span class="form-label">
                所在省份
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.province"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable
                filterable
                @change="onProvinceChange">
                <el-option
                  v-for="item in provinceList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.name"></el-option>
              </el-select>
            </div>
            <div class="form-item">
              <span class="form-label">
                所在城市
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.city"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable
                filterable
                @change="onCityChange">
                <el-option
                  v-for="item in cityList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.name"></el-option>
              </el-select>
            </div>
            <div class="form-item">
              <span class="form-label">
                区/县
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.district"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable
                filterable>
                <el-option
                  v-for="item in districtList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.name"></el-option>
              </el-select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-item form-item-full">
              <span class="form-label">
                详细地址
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.detailAddress"
                placeholder="请输入详细地址"
                size="small"
                clearable />
            </div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                项目建设时间
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-input
                v-model="form.constructionTime"
                placeholder="如2020年"
                size="small"
                clearable />
            </div>
            <div class="form-item">
              <span class="form-label">
                停车位数量
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-input
                v-model="form.parkingSpots"
                type="number"
                placeholder="请输入"
                size="small"
                style="width: 100%" />
            </div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                车位使用情况
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.parkingUsage"
                placeholder="如紧张/充足"
                size="small"
                clearable />
            </div>
            <div class="form-item">
              <span class="form-label">
                停车场信号
                <span class="required">*</span>
              </span>
              <el-select
                v-model="signalArray"
                multiple
                collapse-tags
                placeholder="请选择信号情况"
                size="small"
                style="width: 100%">
                <el-option label="移动信号优" value="移动信号优"></el-option>
                <el-option label="联通信号优" value="联通信号优"></el-option>
                <el-option label="电信信号优" value="电信信号优"></el-option>
                <el-option label="移动信号差" value="移动信号差"></el-option>
                <el-option label="联通信号差" value="联通信号差"></el-option>
                <el-option label="电信信号差" value="电信信号差"></el-option>
              </el-select>
            </div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                停车场结构
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.parkingStructure"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable
                @change="handleParkingStructureChange">
                <el-option label="地上" value="地上"></el-option>
                <el-option label="地下" value="地下"></el-option>
                <el-option label="地上+地下" value="地上+地下"></el-option>
              </el-select>
            </div>
            <div class="form-item">
              <span class="form-label">
                周边200米快充站
                <span class="required">*</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.hasNearbyFastCharge"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable>
                <el-option label="有" value="有"></el-option>
                <el-option label="无" value="无"></el-option>
              </el-select>
            </div>
          </div>

          <div
            v-if="form.parkingStructure === '地上+地下'"
            class="form-row form-row-2"
            style="margin-top: 12px">
            <div class="form-item">
              <span class="form-label">
                地上+地下道闸是否打通
                <span class="required">*</span>
              </span>
              <el-select
                v-model="form.barrierConnected"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable>
                <el-option label="是" value="是"></el-option>
                <el-option label="否" value="否"></el-option>
              </el-select>
            </div>
            <div class="form-item"></div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                私家车位比例
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.privateParkingRatio"
                placeholder="如30%"
                size="small"
                clearable />
            </div>
            <div class="form-item">
              <span class="form-label">
                我司用电性质
                <span class="required">*</span>
              </span>
              <el-select
                v-model="form.electricityUsageType"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable>
                <el-option label="物业取电" value="物业取电"></el-option>
                <el-option label="电力报装" value="电力报装"></el-option>
              </el-select>
            </div>
          </div>

          <!-- 地上车辆情况（停车场结构为地下时隐藏） -->
          <div v-if="form.parkingStructure !== '地下'" class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-top"></i>
              地上车辆情况
            </div>
            <div class="form-row form-row-3">
              <div class="form-item">
                <span class="form-label">地上无桩新能源车（自动计算）</span>
                <span class="stat-text">{{ groundNoPileTotal }} 辆</span>
              </div>
              <div class="form-item">
                <span class="form-label">
                  地上混动车
                  <span class="required">*</span>
                </span>
                <el-input
                  v-model="form.aboveGroundHybridEvCount"
                  type="number"
                  placeholder="请输入"
                  size="small"
                  style="width: 100%" />
              </div>
              <div class="form-item">
                <span class="form-label">
                  地上纯电车
                  <span class="required">*</span>
                </span>
                <el-input
                  v-model="form.aboveGroundPureEvCount"
                  type="number"
                  placeholder="请输入"
                  size="small"
                  style="width: 100%" />
              </div>
            </div>
          </div>

          <!-- 地下车辆情况（停车场结构为地上时隐藏） -->
          <div v-if="form.parkingStructure !== '地上'" class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-bottom"></i>
              地下车辆情况
            </div>
            <div class="form-row form-row-3">
              <div class="form-item">
                <span class="form-label">地下无桩新能源车（自动计算）</span>
                <span class="stat-text">{{ undergroundNoPileTotal }} 辆</span>
              </div>
              <div class="form-item">
                <span class="form-label">
                  地下混动车
                  <span class="required">*</span>
                </span>
                <el-input
                  v-model="form.undergroundHybridEvCount"
                  type="number"
                  placeholder="请输入"
                  size="small"
                  style="width: 100%" />
              </div>
              <div class="form-item">
                <span class="form-label">
                  地下纯电车
                  <span class="required">*</span>
                </span>
                <el-input
                  v-model="form.undergroundPureEvCount"
                  type="number"
                  placeholder="请输入"
                  size="small"
                  style="width: 100%" />
              </div>
            </div>
          </div>

          <!-- 总计 -->
          <div class="sub-group">
            <div class="sub-group-title">
              <i class="el-icon-data-analysis"></i>
              总计
            </div>
            <div class="form-row form-row-3">
              <div class="form-item">
                <span class="form-label">无桩电车总数</span>
                <span class="stat-text">{{ totalNoPileEv }} 辆</span>
              </div>
              <div class="form-item">
                <span class="form-label">混动总数</span>
                <span class="stat-text">{{ totalHybridEv }} 辆</span>
              </div>
              <div class="form-item">
                <span class="form-label">纯电总数</span>
                <span class="stat-text">{{ totalPureEv }} 辆</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-item form-item-full">
              <span class="form-label">
                备注说明
                <span class="form-label-tip">(选填)</span>
              </span>
              <el-input
                v-model="form.remark"
                type="textarea"
                :rows="3"
                placeholder="请输入备注信息"
                size="small" />
            </div>
          </div>
        </div>
      </div>

      <!-- 项目图片 -->
      <div class="section-card" :class="{ collapsed: !expandedSections.photo }">
        <div class="section-header" @click="toggleSection('photo')">
          <div class="section-header-left">
            <div class="section-icon" style="background: #fdf4e8; color: #c9915e">
              <i class="el-icon-picture-outline"></i>
            </div>
            <div>
              <h3 class="section-title">项目图片</h3>
              <p class="section-subtitle">
                上传并分类现场图片，可点击查看
                <span class="photo-spec-link" @click.stop="photoSpecVisible = true">
                  《图片拍摄规范》
                </span>
              </p>
            </div>
          </div>
          <i
            class="el-icon-arrow-down section-arrow"
            :class="{ rotated: !expandedSections.photo }"></i>
        </div>
        <div class="section-content" v-show="expandedSections.photo">
          <div
            class="batch-upload-bar"
            :class="{ disabled: batchUploading || classifying }"
            @click="!batchUploading && !classifying && handleBatchUpload()">
            <i v-if="batchUploading || classifying" class="el-icon-loading"></i>
            <i v-else class="el-icon-upload2"></i>
            <span>
              {{
                batchUploading
                  ? `上传中，请勿退出 ${batchUploadSuccess}/${batchUploadTotal}...`
                  : classifying
                  ? `AI分类中 ${classifyProgress.classified}/${classifyProgress.total}...`
                  : `批量上传图片（已上传 ${totalPhotoCount} 张）`
              }}
            </span>
          </div>

          <!-- 待分类（AI分类中/分类失败，可拖拽到其他分组手动归类） -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                待分类
                <span class="photo-count-tag">{{ (form.unclassifiedPhotos || []).length }} 张</span>
              </span>
              <span class="pending-classify-tip">AI自动分类中，可拖拽到其他分组手动归类</span>
            </div>
            <div v-if="(form.unclassifiedPhotos || []).length" class="photo-grid">
              <div
                v-for="(img, idx) in displayPhotos('unclassifiedPhotos')"
                :key="'unclassified-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('unclassifiedPhotos', idx)"
                @dragstart="handlePhotoDragStart('unclassifiedPhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-delete" @click.stop="removePhoto('unclassifiedPhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.unclassifiedPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('unclassifiedPhotos')">
                <i
                  :class="
                    isExpanded('unclassifiedPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('unclassifiedPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <!-- 项目现场图 -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                项目现场图
                <span class="photo-count-tag">{{ (form.scenePhotos || []).length }} 张</span>
              </span>
              <button class="clear-btn" @click="clearPhotoList('scenePhotos')" type="button">
                <i class="el-icon-delete"></i>
                清空
              </button>
            </div>
            <div
              class="photo-grid"
              :class="{ 'drag-over': dragOverCategory === 'scenePhotos' }"
              @dragover.prevent
              @dragenter="dragOverCategory = 'scenePhotos'"
              @dragleave="dragOverCategory = ''"
              @drop="handlePhotoDrop('scenePhotos', $event)">
              <div class="photo-add" @click="openPhotoUploader('scenePhotos')">
                <i class="el-icon-plus"></i>
                <span>添加</span>
              </div>
              <div
                v-for="(img, idx) in displayPhotos('scenePhotos')"
                :key="'scene-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('scenePhotos', idx)"
                @dragstart="handlePhotoDragStart('scenePhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-delete" @click.stop="removePhoto('scenePhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.scenePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('scenePhotos')">
                <i :class="isExpanded('scenePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'"></i>
                {{ isExpanded('scenePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <!-- 无桩电车图 -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                无桩电车图
                <span class="photo-count-tag">{{ (form.evPhotos || []).length }} 张</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI识别
                </span>
              </span>
              <button class="clear-btn" @click="clearPhotoList('evPhotos')" type="button">
                <i class="el-icon-delete"></i>
                清空
              </button>
            </div>
            <div
              class="photo-grid"
              :class="{ 'drag-over': dragOverCategory === 'evPhotos' }"
              @dragover.prevent
              @dragenter="dragOverCategory = 'evPhotos'"
              @dragleave="dragOverCategory = ''"
              @drop="handlePhotoDrop('evPhotos', $event)">
              <div class="photo-add" @click="openPhotoUploader('evPhotos')">
                <i class="el-icon-plus"></i>
                <span>添加</span>
              </div>
              <div
                v-for="(img, idx) in displayPhotos('evPhotos')"
                :key="'ev-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('evPhotos', idx)"
                @dragstart="handlePhotoDragStart('evPhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-location-tags" v-if="form.parkingStructure">
                  <span
                    class="loc-tag ground-tag"
                    :class="{
                      active: getEvLocationValue(idx) === '地上',
                      inactive: getEvLocationValue(idx) !== '地上',
                    }"
                    @click.stop="setEvLocation(idx, '地上')">
                    地上
                  </span>
                  <span
                    class="loc-tag underground-tag"
                    :class="{
                      active: getEvLocationValue(idx) === '地下',
                      inactive: getEvLocationValue(idx) !== '地下',
                    }"
                    @click.stop="setEvLocation(idx, '地下')">
                    地下
                  </span>
                </div>
                <div
                  class="ev-info-tags"
                  v-if="
                    (form.evPhotoBrands && form.evPhotoBrands[idx]) ||
                    (form.evPhotoModels && form.evPhotoModels[idx]) ||
                    (form.evPhotoLicensePlates && form.evPhotoLicensePlates[idx])
                  ">
                  <span class="ev-info-tag" v-if="form.evPhotoBrands && form.evPhotoBrands[idx]">
                    {{ form.evPhotoBrands[idx] }}
                  </span>
                  <span class="ev-info-tag" v-if="form.evPhotoModels && form.evPhotoModels[idx]">
                    {{ form.evPhotoModels[idx] }}
                  </span>
                  <span
                    class="ev-info-tag"
                    v-if="form.evPhotoLicensePlates && form.evPhotoLicensePlates[idx]">
                    {{ form.evPhotoLicensePlates[idx] }}
                  </span>
                </div>
                <div class="photo-delete" @click.stop="removePhoto('evPhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.evPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('evPhotos')">
                <i :class="isExpanded('evPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'"></i>
                {{ isExpanded('evPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
            <div class="photo-tip" v-if="form.parkingStructure === '地上+地下'">
              <i class="el-icon-info"></i>
              <span>点击标签选择地上/地下位置，未选择时两个标签置灰</span>
            </div>
          </div>

          <!-- 私人充电桩图 -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                私人充电桩图
                <span class="photo-count-tag">{{ (form.privatePilePhotos || []).length }} 张</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI识别
                </span>
              </span>
              <button class="clear-btn" @click="clearPhotoList('privatePilePhotos')" type="button">
                <i class="el-icon-delete"></i>
                清空
              </button>
            </div>
            <div
              class="photo-grid"
              :class="{ 'drag-over': dragOverCategory === 'privatePilePhotos' }"
              @dragover.prevent
              @dragenter="dragOverCategory = 'privatePilePhotos'"
              @dragleave="dragOverCategory = ''"
              @drop="handlePhotoDrop('privatePilePhotos', $event)">
              <div class="photo-add" @click="openPhotoUploader('privatePilePhotos')">
                <i class="el-icon-plus"></i>
                <span>添加</span>
              </div>
              <div
                v-for="(img, idx) in displayPhotos('privatePilePhotos')"
                :key="'private-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('privatePilePhotos', idx)"
                @dragstart="handlePhotoDragStart('privatePilePhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-delete" @click.stop="removePhoto('privatePilePhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.privatePilePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('privatePilePhotos')">
                <i
                  :class="
                    isExpanded('privatePilePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('privatePilePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <!-- 竞品充电桩图 -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                竞品充电桩图
                <span class="photo-count-tag">{{ (form.competePilePhotos || []).length }} 张</span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI识别
                </span>
              </span>
              <button class="clear-btn" @click="clearPhotoList('competePilePhotos')" type="button">
                <i class="el-icon-delete"></i>
                清空
              </button>
            </div>
            <div
              class="photo-grid"
              :class="{ 'drag-over': dragOverCategory === 'competePilePhotos' }"
              @dragover.prevent
              @dragenter="dragOverCategory = 'competePilePhotos'"
              @dragleave="dragOverCategory = ''"
              @drop="handlePhotoDrop('competePilePhotos', $event)">
              <div class="photo-add" @click="openPhotoUploader('competePilePhotos')">
                <i class="el-icon-plus"></i>
                <span>添加</span>
              </div>
              <div
                v-for="(img, idx) in displayPhotos('competePilePhotos')"
                :key="'compete-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('competePilePhotos', idx)"
                @dragstart="handlePhotoDragStart('competePilePhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-delete" @click.stop="removePhoto('competePilePhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.competePilePhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('competePilePhotos')">
                <i
                  :class="
                    isExpanded('competePilePhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('competePilePhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <!-- 竞品情况截图 -->
          <div class="photo-block">
            <div class="photo-block-header">
              <span class="form-label">
                竞品情况截图
                <span class="photo-count-tag">
                  {{ (form.competeScreenshotPhotos || []).length }} 张
                </span>
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI识别
                </span>
              </span>
              <button
                class="clear-btn"
                @click="clearPhotoList('competeScreenshotPhotos')"
                type="button">
                <i class="el-icon-delete"></i>
                清空
              </button>
            </div>
            <div
              class="photo-grid"
              :class="{ 'drag-over': dragOverCategory === 'competeScreenshotPhotos' }"
              @dragover.prevent
              @dragenter="dragOverCategory = 'competeScreenshotPhotos'"
              @dragleave="dragOverCategory = ''"
              @drop="handlePhotoDrop('competeScreenshotPhotos', $event)">
              <div class="photo-add" @click="openPhotoUploader('competeScreenshotPhotos')">
                <i class="el-icon-plus"></i>
                <span>添加</span>
              </div>
              <div
                v-for="(img, idx) in displayPhotos('competeScreenshotPhotos')"
                :key="'screenshot-' + idx"
                class="photo-item"
                draggable="true"
                @click="handlePreviewPhoto('competeScreenshotPhotos', idx)"
                @dragstart="handlePhotoDragStart('competeScreenshotPhotos', idx, $event)"
                @dragend="handlePhotoDragEnd">
                <el-image :src="img" fit="cover" class="photo-img" @dragstart.native.prevent />
                <div class="photo-delete" @click.stop="removePhoto('competeScreenshotPhotos', idx)">
                  <i class="el-icon-close"></i>
                </div>
              </div>
              <div
                v-if="form.competeScreenshotPhotos.length >= MAX_DISPLAY_PHOTOS"
                class="photo-more"
                @click="toggleExpandPhotos('competeScreenshotPhotos')">
                <i
                  :class="
                    isExpanded('competeScreenshotPhotos') ? 'el-icon-arrow-up' : 'el-icon-more'
                  "></i>
                {{ isExpanded('competeScreenshotPhotos') ? '收起' : '查看全部' }}
              </div>
            </div>
          </div>

          <div class="upload-tip-box">
            <i class="el-icon-lightbulb"></i>
            <span>批量上传后，AI将自动识别并分类图片。支持拖拽图片到不同分类区域调整分类。</span>
          </div>
        </div>
      </div>

      <!-- 竞品信息 -->
      <div class="section-card" :class="{ collapsed: !expandedSections.compete }">
        <div class="section-header" @click="toggleSection('compete')">
          <div class="section-header-left">
            <div class="section-icon" style="background: #fff7ed; color: #f59e0b">
              <i class="el-icon-coin"></i>
            </div>
            <div>
              <h3 class="section-title">竞品信息</h3>
              <p class="section-subtitle">周边竞品充电桩情况</p>
            </div>
          </div>
          <i
            class="el-icon-arrow-down section-arrow"
            :class="{ rotated: !expandedSections.compete }"></i>
        </div>
        <div class="section-content" v-show="expandedSections.compete">
          <div v-for="(item, index) in form.competitors" :key="index" class="competitor-card">
            <div class="competitor-header">
              <span class="competitor-title">竞品 {{ index + 1 }}</span>
              <el-button
                type="text"
                class="competitor-remove"
                icon="el-icon-delete"
                size="mini"
                @click="removeCompetitor(index)">
                删除
              </el-button>
            </div>
            <div class="form-row form-row-2">
              <div class="form-item">
                <span class="form-label">品牌名称</span>
                <el-input v-model="item.brandName" placeholder="如特来电" size="small" clearable />
              </div>
              <div class="form-item">
                <span class="form-label">充电桩数量</span>
                <el-input
                  v-model="item.pileCount"
                  type="number"
                  placeholder="请输入"
                  size="small"
                  style="width: 100%" />
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-item">
                <span class="form-label">电价模式</span>
                <el-input v-model="item.priceMode" placeholder="如峰谷平" size="small" clearable />
              </div>
              <div class="form-item">
                <span class="form-label">使用率</span>
                <el-input v-model="item.usageRate" placeholder="如60%" size="small" clearable />
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-item">
                <span class="form-label">占位率</span>
                <el-input v-model="item.occupancyRate" placeholder="如10%" size="small" clearable />
              </div>
              <div class="form-item">
                <span class="form-label">收费</span>
                <el-input
                  v-model="item.chargingFee"
                  placeholder="如1.2元/度"
                  size="small"
                  clearable />
              </div>
            </div>
            <div class="form-row form-row-2">
              <div class="form-item">
                <span class="form-label">充电桩位置</span>
                <el-input
                  v-model="item.pileLocation"
                  placeholder="如B1层停车场入口"
                  size="small"
                  clearable />
              </div>
              <div class="form-item">
                <span class="form-label">详细地址</span>
                <el-input
                  v-model="item.detailAddress"
                  placeholder="请输入详细地址"
                  size="small"
                  clearable />
              </div>
            </div>
            <div class="form-row">
              <div class="form-item form-item-full">
                <span class="form-label">说明</span>
                <el-input
                  v-model="item.description"
                  type="textarea"
                  :rows="2"
                  placeholder="请输入竞品说明"
                  size="small" />
              </div>
            </div>
          </div>
          <el-button
            class="add-competitor-btn"
            icon="el-icon-plus"
            size="small"
            @click="addCompetitor">
            添加竞品
          </el-button>
        </div>
      </div>

      <!-- 私桩信息 -->
      <div class="section-card" :class="{ collapsed: !expandedSections.pile }">
        <div class="section-header" @click="toggleSection('pile')">
          <div class="section-header-left">
            <div class="section-icon" style="background: #ecfdf5; color: #10b981">
              <i class="el-icon-finished"></i>
            </div>
            <div>
              <h3 class="section-title">私桩信息</h3>
              <p class="section-subtitle">私人充电桩情况</p>
            </div>
          </div>
          <i
            class="el-icon-arrow-down section-arrow"
            :class="{ rotated: !expandedSections.pile }"></i>
        </div>
        <div class="section-content" v-show="expandedSections.pile">
          <div class="form-row form-row-3">
            <div class="form-item">
              <span class="form-label">
                有无私桩
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-select
                v-model="form.hasPrivatePile"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable>
                <el-option label="有" value="有"></el-option>
                <el-option label="无" value="无"></el-option>
              </el-select>
            </div>
            <div class="form-item">
              <span class="form-label">
                私桩数量
                <span class="ai-badge">
                  <i class="el-icon-magic-stick"></i>
                  AI
                </span>
              </span>
              <el-input
                v-model="form.privatePileCount"
                type="number"
                placeholder="数量"
                size="small"
                style="width: 100%" />
            </div>
            <div class="form-item">
              <span class="form-label">私桩位置</span>
              <el-select
                v-model="form.privatePileLocation"
                placeholder="请选择"
                size="small"
                style="width: 100%"
                clearable>
                <el-option label="地上" value="地上"></el-option>
                <el-option label="地下" value="地下"></el-option>
                <el-option label="地下+地上" value="地下+地上"></el-option>
              </el-select>
            </div>
          </div>
        </div>
      </div>

      <!-- 项目总结 -->
      <div class="section-card" :class="{ collapsed: !expandedSections.summary }">
        <div class="section-header" @click="toggleSection('summary')">
          <div class="section-header-left">
            <div class="section-icon" style="background: #f3e8ff; color: #8b5cf6">
              <i class="el-icon-document"></i>
            </div>
            <div>
              <h3 class="section-title">项目总结</h3>
              <p class="section-subtitle">布桩建议与核场总结</p>
            </div>
          </div>
          <i
            class="el-icon-arrow-down section-arrow"
            :class="{ rotated: !expandedSections.summary }"></i>
        </div>
        <div class="section-content" v-show="expandedSections.summary">
          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                布桩公式计算数量
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.formulaPileCount"
                type="number"
                placeholder="请输入"
                size="small"
                style="width: 100%" />
            </div>
            <div class="form-item">
              <span class="form-label">城市最低下限数量</span>
              <el-input
                v-model="form.cityMinPileCount"
                type="number"
                placeholder="请输入"
                size="small"
                style="width: 100%" />
            </div>
          </div>

          <div class="form-row form-row-2">
            <div class="form-item">
              <span class="form-label">
                建议安装数量
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.suggestedPileCount"
                type="number"
                placeholder="请输入建议安装数量"
                size="small"
                style="width: 100%" />
            </div>
            <div class="form-item">
              <span class="form-label">
                建议安装位置
                <span class="required">*</span>
              </span>
              <el-input
                v-model="form.suggestedInstallLocation"
                placeholder="请输入建议安装位置"
                size="small"
                clearable />
            </div>
          </div>

          <div class="form-row">
            <div class="form-item form-item-full">
              <div class="form-label-row">
                <span class="form-label">
                  项目核场总结
                  <span class="required">*</span>
                </span>
                <button
                  class="ai-btn"
                  @click="handleGenerateSummary"
                  type="button"
                  :disabled="summaryGenerating">
                  <i class="el-icon-magic-stick" :class="{ 'is-loading': summaryGenerating }"></i>
                  {{ summaryGenerating ? '生成中...' : '一键生成' }}
                </button>
              </div>
              <el-input
                v-model="form.summary"
                type="textarea"
                :autosize="{ minRows: 4, maxRows: 16 }"
                placeholder="点击一键生成按钮自动生成项目核场总结"
                size="small" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件上传 input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      multiple
      style="display: none"
      @change="handleFileChange" />
    <!-- 隐藏的批量上传 input -->
    <input
      ref="batchFileInput"
      type="file"
      accept="image/*"
      multiple
      style="display: none"
      @change="handleBatchFileChange" />

    <!-- 取消确认弹窗 -->
    <el-dialog
      title="确认取消？"
      :visible.sync="showCancelDialog"
      width="400px"
      :show-close="false">
      <div class="confirm-content">
        <div class="confirm-icon danger-icon">
          <i class="el-icon-warning"></i>
        </div>
        <h3 class="confirm-title">确认取消编辑？</h3>
        <p class="confirm-desc">取消后操作将不被保存，确认是否取消？</p>
      </div>
      <span slot="footer">
        <el-button size="small" @click="showCancelDialog = false">继续编辑</el-button>
        <el-button size="small" type="primary" @click="confirmCancel">确认取消</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog
      :visible.sync="previewVisible"
      :show-close="true"
      append-to-body
      custom-class="photo-preview-dialog"
      width="80%"
      @click.native="previewVisible = false">
      <img
        v-if="previewSrc"
        :src="previewSrc"
        style="width: 100%; max-height: 75vh; object-fit: contain"
        @click.stop />
    </el-dialog>

    <!-- 图片拍摄规范弹窗 -->
    <el-dialog
      title="交付现场图片拍摄采集规范"
      :visible.sync="photoSpecVisible"
      width="680px"
      append-to-body>
      <div class="photo-spec-body">
        <p class="spec-block-title">概要</p>
        <p class="spec-paragraph">
          为降低AI识别误差，从源头减少混合画面、模糊画面、无效画面，统一交付人员拍摄标准，分类目明确拍摄要求、拍摄要点，适配交付快速拍照作业。
        </p>
        <p class="spec-block-title">1、通用拍摄基础要求（所有图片统一执行）</p>
        <p class="spec-li">• 清晰度：禁止模糊、抖动、逆光过曝、全黑拍摄，保证主体轮廓清晰可识别</p>
        <p class="spec-li">• 构图：主体居中拍摄，单一图片聚焦一个核心主体，尽量减少多物体同框</p>
        <p class="spec-li">• 角度：平视拍摄为主，禁止俯拍、仰拍导致主体变形；无需修图、无需裁剪</p>
        <p class="spec-block-title">2、各类目图片专项拍摄标准</p>
        <p class="spec-sub-title">2.1 新能源无桩电车拍摄要求</p>
        <p class="spec-li">• 核心拍摄要素：清晰拍摄新能源绿牌及整体车身，保证新能源身份可识别</p>
        <p class="spec-li">• 硬性要求：</p>
        <p class="spec-li spec-li-sub">- 画面内尽量不要出现任何充电桩设备，纯车辆画面</p>
        <p class="spec-li spec-li-sub">
          - 需要使用水印相机添加车辆停放位置备注，备注信息分为地上或地下
        </p>
        <p class="spec-li">• 拍摄范围：整车拍摄即可，无需特写局部零部件</p>
        <p class="spec-sub-title">2.2 公共充电桩拍摄要求</p>
        <p class="spec-li">
          •
          核心拍摄要素：充电桩整机+机身上场站编号/品牌LOGO/计费二维码（电桩画面占据图片的主体内容并居中）
        </p>
        <p class="spec-li">• 规避点：尽量不要将新能源电车完整纳入画面</p>
        <p class="spec-sub-title">2.3 私人充电桩拍摄要求</p>
        <p class="spec-li">• 核心拍摄要素：充电桩整机（电桩画面占据图片的主体内容并居中）</p>
        <p class="spec-li">• 规避点：尽量不要将新能源电车完整纳入画面</p>
        <p class="spec-sub-title">2.4 其他现场图片拍摄要求</p>
        <p class="spec-li">• 拍摄范围：停车场场地、土建基础、配电设施、通道、道闸等配套环境</p>
        <p class="spec-li">
          • 要求：完整记录场地工况，无需刻意规避远处车辆，只要画面无清晰充电桩、无清晰电车主体即可
        </p>
        <p class="spec-sub-title">2.5 竞品公桩平台手机截图或拍摄要求</p>
        <p class="spec-li">
          • 截图/拍摄图片要素：尽量包含品牌，电桩数量、类型、状态，位置，价格信息
        </p>
        <p class="spec-li">
          •
          采集方式：尽量直接使用手机截图，如采用拍摄方式，需注意手机平放，垂直拍摄屏幕，减少屏幕反光保证图片清晰；禁止实景和手机屏幕同框拍摄
        </p>
        <p class="spec-li">• 数量：每个平台界面至少留存1～2张截图/照片</p>
        <p class="spec-block-title">3、现场拍照作业流程（交付人员标准化步骤）</p>
        <p class="spec-li">
          第一步：单独拍摄场内新能源无桩电车，避开充电桩，并通过水印相机功能添加【停放位置】信息地上或地下！
        </p>
        <p class="spec-li">第二步：分桩拍照：公桩、私桩分开独立拍摄，车桩尽量不同框</p>
        <p class="spec-li">第三步：拍摄停车场场地、配套土建等其他现场图片</p>
        <p class="spec-li">
          第四步：最后截图或拍摄竞品手机平台（如有），拍摄时单独拍摄不和实景同框
        </p>
      </div>
      <span slot="footer">
        <el-button size="small" type="primary" @click="photoSpecVisible = false">知道了</el-button>
      </span>
    </el-dialog>
  </scroll-layout>
</template>

<script>
  import {
    inspectionRecordDetail,
    inspectionRecordSave,
    inspectionRecordSubmit,
    inspectionRecordAiFillProjectInfo,
    inspectionRecordGetLocationCity,
    inspectionRecordCheckProjectNameExists,
    inspectionRecordSaveAndClassifyImages,
    inspectionRecordOssDirectUploadPolicy,
    inspectionRecordGetPhotoNames,
    inspectionRecordDeletePhotos,
    inspectionRecordGetClassifyProgress,
    inspectionRecordUpdateImageLocation,
    inspectionRecordUpdateImageCategory,
    treeUtils,
  } from '@/service/apis';

  // 图片分类映射（常量，不放在 methods 中）
  const PHOTO_CATEGORY_MAP = {
    scenePhotos: 'SCENE',
    evPhotos: 'EV',
    privatePilePhotos: 'PRIVATE_PILE',
    competePilePhotos: 'COMPETE_PILE',
    competeScreenshotPhotos: 'COMPETE_SCREENSHOT',
  };

  // 每个类目默认最多展示数量
  const MAX_DISPLAY_PHOTOS = 20;

  // 图片去重集合（按分类存储，避免跨分类误拦截）
  const uploadedPhotoIdentitiesByCategory = {};
  // 全局去重集合（仅批量上传使用）
  const uploadedPhotoIdentities = new Set();
  // 服务端已有照片名称集合（每次上传前从后端获取）
  let serverPhotoNames = new Set();

  // 页面展示URL（本地blob或服务端URL）-> 照片元信息（ossKey/photoName/identity），
  // 用于点叉删除时同步软删服务端照片记录并清理本地去重集合
  const photoMetaByUrl = {};

  // URL 归一化为 OSS objectKey（去 query、取 pathname），与后端 normalizeProjectVerificationOssKey 对应
  const normalizeOssKey = url => {
    if (!url) return '';
    const noQuery = String(url).split('?')[0];
    if (/^https?:\/\//i.test(noQuery)) {
      try {
        // pathname 保持 percent-encoding（后端签名URL会把key中的"/"编码为%2F），需解码还原真实objectKey，
        // 否则点叉删除时与服务端存储的photoUrl不匹配导致软删失效
        return decodeURIComponent(new URL(noQuery).pathname.replace(/^\//, ''));
      } catch (e) {
        return noQuery.replace(/^\//, '');
      }
    }
    return noQuery.replace(/^\//, '');
  };

  const defaultForm = () => ({
    id: null,
    projectName: '',
    stationName: '',
    siteNature: '',
    province: '',
    city: '',
    district: '',
    detailAddress: '',
    surveyTime: null,
    constructionTime: '',
    parkingSpots: null,
    parkingUsage: '',
    parkingSignal: '',
    parkingStructure: '',
    barrierConnected: '',
    hasNearbyFastCharge: '',
    privateParkingRatio: '',
    electricityUsageType: '',
    aboveGroundHybridEvCount: null,
    aboveGroundPureEvCount: null,
    undergroundHybridEvCount: null,
    undergroundPureEvCount: null,
    unscopedNoPileEvCount: null,
    unscopedHybridEvCount: null,
    unscopedPureEvCount: null,
    remark: '',
    scenePhotos: [],
    evPhotos: [],
    evPhotoLocations: [],
    evPhotoCarTypes: [],
    evPhotoBrands: [],
    evPhotoModels: [],
    evPhotoLicensePlates: [],
    privatePilePhotos: [],
    competePilePhotos: [],
    competeScreenshotPhotos: [],
    unclassifiedPhotos: [],
    hasPrivatePile: '',
    privatePileCount: null,
    privatePileLocation: '',
    formulaPileCount: null,
    cityMinPileCount: null,
    suggestedPileCount: null,
    suggestedInstallLocation: '',
    summary: '',
    competitors: [],
    status: '',
  });

  const defaultCompetitor = () => ({
    brandName: '',
    pileCount: null,
    priceMode: '',
    usageRate: '',
    occupancyRate: '',
    chargingFee: '',
    pileLocation: '',
    detailAddress: '',
    description: '',
    sortOrder: null,
  });

  export default {
    name: 'InspectionRecordEdit',
    data() {
      return {
        MAX_DISPLAY_PHOTOS,
        loading: false,
        locatingCity: false,
        locationCity: '',
        saving: false,
        submitting: false,
        showCancelDialog: false,
        photoSpecVisible: false,
        currentUploadKey: '',
        batchUploading: false,
        batchUploadTotal: 0,
        batchUploadSuccess: 0,
        classifying: false,
        classifyProgress: { total: 0, classified: 0, unclassified: 0 },
        // 轮询开始前记录的整记录进度基线：进度接口返回的是整个记录的计数（含历史照片），
        // 展示时按本次批次换算增量，避免上传1张后进度从1/1跳变成11/12
        classifyProgressBaseline: { total: 0, classified: 0 },
        classifyPollTimer: null,
        dragSource: null,
        dragOverCategory: '',
        summaryGenerating: false,
        previewVisible: false,
        previewSrc: '',
        previewList: [],
        detail: {},
        form: defaultForm(),
        expandedSections: {
          basic: true,
          photo: true,
          compete: false,
          pile: false,
          summary: true,
        },
        provinceList: [],
        cityList: [],
        districtList: [],
        allDistrictData: [],
        expandedCategories: [], // 已展开的照片类目
        ossPolicyCache: null,
        ossPolicyExpireTime: 0,
      };
    },
    computed: {
      isEdit() {
        return !!this.$route.query.id;
      },
      // 定位城市下拉：按省份分组的全国城市列表，供手动选择
      locationCityGroups() {
        return (this.allDistrictData || [])
          .map(p => ({
            province: p.name,
            cities: (p.subNodes || []).map(c => ({ id: c.id, name: c.name })),
          }))
          .filter(group => group.cities.length > 0);
      },
      isSubmitted() {
        // 已提交、已提交立项的核场记录均不可再保存草稿
        return ['SUBMITTED', 'INITIATION_SUBMITTED'].includes(
          String(this.form.status).toUpperCase()
        );
      },
      // 是否为已提交立项状态（立项审批流程中，不可再编辑提交）
      isInitiatedRecord() {
        return String(this.form.status).toUpperCase() === 'INITIATION_SUBMITTED';
      },
      // 无桩新能源车 = 混动车 + 纯电车（自动计算，不可填写）
      groundNoPileTotal() {
        return (
          Number(this.form.aboveGroundHybridEvCount || 0) +
          Number(this.form.aboveGroundPureEvCount || 0)
        );
      },
      undergroundNoPileTotal() {
        return (
          Number(this.form.undergroundHybridEvCount || 0) +
          Number(this.form.undergroundPureEvCount || 0)
        );
      },
      totalNoPileEv() {
        return (
          this.groundNoPileTotal +
          this.undergroundNoPileTotal +
          Number(this.form.unscopedNoPileEvCount || 0)
        );
      },
      totalHybridEv() {
        return (
          Number(this.form.aboveGroundHybridEvCount || 0) +
          Number(this.form.undergroundHybridEvCount || 0) +
          Number(this.form.unscopedHybridEvCount || 0)
        );
      },
      totalPureEv() {
        return (
          Number(this.form.aboveGroundPureEvCount || 0) +
          Number(this.form.undergroundPureEvCount || 0) +
          Number(this.form.unscopedPureEvCount || 0)
        );
      },
      totalPhotoCount() {
        return (
          (this.form.scenePhotos || []).length +
          (this.form.evPhotos || []).length +
          (this.form.privatePilePhotos || []).length +
          (this.form.competePilePhotos || []).length +
          (this.form.competeScreenshotPhotos || []).length
        );
      },
      signalArray: {
        get() {
          if (!this.form.parkingSignal) return [];
          return this.form.parkingSignal.split(',').filter(Boolean);
        },
        set(val) {
          this.form.parkingSignal = (val || []).join(',');
        },
      },
    },
    watch: {
      '$route.query.id': {
        immediate: true,
        handler(val) {
          if (val) {
            this.loadDetail();
          } else {
            this.form = defaultForm();
            this.detail = {};
          }
        },
      },
      // 省市区基础数据加载完成后，自动回填下拉列表
      allDistrictData(data) {
        if (data && data.length && this.form.province) {
          this.populateCityDistrictLists();
        }
      },
    },
    created() {
      this.loadDistrictData();
    },
    beforeDestroy() {
      // 离开页面停止分类进度轮询，避免空转请求
      if (this.classifyPollTimer) {
        clearTimeout(this.classifyPollTimer);
        this.classifyPollTimer = null;
      }
    },
    mounted() {
      this.locateCity();
      this.$nextTick(() => {
        const input = this.$refs.projectNameInput;
        if (input && input.$el) {
          const el = input.$el.querySelector('input');
          if (el) {
            el.addEventListener('blur', () => {
              this.checkProjectNameDuplicate();
            });
          }
        }
      });
    },
    methods: {
      // 获取限制数量的照片列表（展开时返回全部）
      displayPhotos(key) {
        const photos = this.form[key] || [];
        if (this.expandedCategories.includes(key)) return photos;
        return photos.slice(0, MAX_DISPLAY_PHOTOS);
      },
      // 切换展开/收起照片
      toggleExpandPhotos(key) {
        const idx = this.expandedCategories.indexOf(key);
        if (idx > -1) {
          this.expandedCategories.splice(idx, 1);
        } else {
          this.expandedCategories.push(key);
        }
      },
      // 是否已展开
      isExpanded(key) {
        return this.expandedCategories.includes(key);
      },
      goBack() {
        this.$router.push({ name: 'inspectionRecordList' });
      },
      // 手动选择定位城市（与自动定位互不影响，仅作展示参考）
      onLocationCityChange(city) {
        this.locationCity = city || '';
      },
      locateCity() {
        if (!navigator.geolocation) {
          this.locationCity = '暂不支持定位';
          return;
        }
        this.locatingCity = true;
        navigator.geolocation.getCurrentPosition(
          position => {
            inspectionRecordGetLocationCity({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            })
              .then(result => {
                this.locationCity = (result && result.city) || '未获取到城市';
              })
              .catch(() => {
                this.locationCity = '城市解析失败';
              })
              .finally(() => {
                this.locatingCity = false;
              });
          },
          () => {
            this.locationCity = '定位失败，请检查授权';
            this.locatingCity = false;
          }
        );
      },
      handleCancel() {
        this.showCancelDialog = true;
      },
      confirmCancel() {
        this.showCancelDialog = false;
        this.$router.push({ name: 'inspectionRecordList' });
      },
      toggleSection(key) {
        this.expandedSections[key] = !this.expandedSections[key];
      },
      handleParkingStructureChange() {
        if (this.form.parkingStructure !== '地上+地下') {
          this.form.barrierConnected = '';
          // 地上/地下时，自动设置所有无桩电车照片位置
          const loc = this.form.parkingStructure; // '地上' or '地下'
          if (loc === '地上' || loc === '地下') {
            if (!this.form.evPhotoLocations) this.form.evPhotoLocations = [];
            if (!this.form.evPhotoCarTypes) this.form.evPhotoCarTypes = [];
            this.form.evPhotos.forEach((_, idx) => {
              this.$set(this.form.evPhotoLocations, idx, loc);
              this.$set(this.form.evPhotoCarTypes, idx, loc === '地下' ? 2 : 1);
            });
          }
        }
        // 与车辆情况模块联动：清空被隐藏一侧的数量，避免隐藏数据流入总计/总结
        if (this.form.parkingStructure === '地上') {
          this.form.undergroundHybridEvCount = null;
          this.form.undergroundPureEvCount = null;
        } else if (this.form.parkingStructure === '地下') {
          this.form.aboveGroundHybridEvCount = null;
          this.form.aboveGroundPureEvCount = null;
        }
      },
      cleanAiValue(val) {
        if (
          val === -1 ||
          val === '-1' ||
          val === '未获取到' ||
          val === 0 ||
          val === '0' ||
          val === 'null' ||
          val === 'None'
        )
          return undefined;
        return val;
      },
      fillFormFromAiResult(data = {}) {
        const fields = [
          'siteNature',
          'province',
          'city',
          'district',
          'constructionTime',
          'parkingSpots',
          'parkingStructure',
          'hasNearbyFastCharge',
        ];
        fields.forEach(field => {
          let raw = data[field];
          // 周边200米快充站：AI返回编码1/0，转换为枚举有/无
          if (field === 'hasNearbyFastCharge') {
            if (raw === 1 || raw === '1') raw = '有';
            else if (raw === 0 || raw === '0') raw = '无';
          }
          const value = this.cleanAiValue(raw);
          if (value !== undefined && value !== null && value !== '') {
            this.$set(this.form, field, value);
          }
        });
        if (this.form.projectName) {
          this.form.stationName = this.form.stationName || this.form.projectName;
        }
        // AI填充省市区后，联动刷新下拉列表（不清空已填值）
        this.$nextTick(() => this.populateCityDistrictLists());
      },
      async checkProjectNameDuplicate() {
        const projectName = this.form.projectName && this.form.projectName.trim();
        if (!projectName) return true;
        // 编辑模式下不检查
        if (this.form.id) return true;
        try {
          const exists = await inspectionRecordCheckProjectNameExists({ projectName });
          if (exists) {
            return new Promise(resolve => {
              this.$confirm(`项目名称「${projectName}」已存在核场记录，是否继续新增？`, '提示', {
                confirmButtonText: '是，继续新增',
                cancelButtonText: '否',
                type: 'warning',
              })
                .then(() => resolve(true))
                .catch(() => {
                  this.$router.push({ name: 'inspectionRecordList' });
                  resolve(false);
                });
            });
          }
        } catch (err) {
          console.error('检查项目名称失败:', err);
        }
        return true;
      },
      async handleAiUpdate() {
        const projectName = this.form.projectName && this.form.projectName.trim();
        if (!projectName) {
          this.$message.warning('请先输入项目名称');
          return;
        }
        const canProceed = await this.checkProjectNameDuplicate();
        if (!canProceed) return;
        // 与小程序端一致：定位城市拼在项目名前提升检索精度
        const searchName = this.locationCity ? `${this.locationCity}${projectName}` : projectName;
        this.doAiUpdate(searchName);
      },
      doAiUpdate(projectName) {
        this.$message.info('AI 检索更新中...');
        inspectionRecordAiFillProjectInfo({ projectName })
          .then(res => {
            this.fillFormFromAiResult(res || {});
            // AI 填充完成后，若还没有记录 ID，自动保存草稿获取 ID
            if (!this.form.id) {
              this.saving = true;
              inspectionRecordSave(this.buildSubmitData())
                .then(id => {
                  if (id) {
                    this.form.id = id;
                    this.$router.replace({ name: 'inspectionRecordEdit', query: { id } });
                  }
                  this.$message.success('AI 检索更新成功');
                })
                .catch(err => {
                  console.error('自动保存草稿失败:', err);
                  this.$message.error('AI 填充成功，但保存草稿失败');
                })
                .finally(() => {
                  this.saving = false;
                });
            } else {
              this.$message.success('AI 检索更新成功');
            }
          })
          .catch(err => {
            console.error('AI 检索更新失败:', err);
            this.$message.error('AI 检索更新失败');
          });
      },
      handleGenerateSummary() {
        if (this.summaryGenerating) return;
        const f = this.form;
        if (!f.projectName || !f.projectName.trim()) {
          this.$message.warning('请先填写项目名称后再生成总结');
          return;
        }
        if (!f.siteNature || !f.siteNature.trim()) {
          this.$message.warning('请先填写站点性质后再生成总结');
          return;
        }
        // 计算竞品电桩总数（el-input绑定为字符串，需转数字避免拼接）
        const competitorPileCount = (f.competitors || []).reduce(
          (sum, item) => sum + (Number(item.pileCount) || 0),
          0
        );
        // 拼接总结内容（格式与小程序端保持一致）
        const projectName = f.projectName.trim();
        const siteNature = f.siteNature;
        const groundPureEv = Number(f.aboveGroundPureEvCount || 0);
        const groundHybrid = Number(f.aboveGroundHybridEvCount || 0);
        const undergroundPureEv = Number(f.undergroundPureEvCount || 0);
        const undergroundHybrid = Number(f.undergroundHybridEvCount || 0);
        const privatePile = Number(f.privatePileCount) || 0;
        const parkingStructure = f.parkingStructure || '';
        const parkingSpots = Number(f.parkingSpots) || 0;
        const privateRatio = f.privateParkingRatio || '';
        const barrier = f.barrierConnected || '';

        const lines = [];
        // 第一条：项目名称 + 项目类型
        lines.push(`- ${projectName}为${siteNature}项目`);
        // 第二条：停车场地、停车位及私家车位（私家车位 = 停车位*占比，占比可解析时直接算出结果，为空时不显示*）
        const ratioNum = parseFloat(privateRatio);
        const privateSpotsText = !Number.isNaN(ratioNum)
          ? `${Math.round((parkingSpots * ratioNum) / 100)}个`
          : `${parkingSpots}${privateRatio ? `*${privateRatio}` : ''}个`;
        lines.push(
          `- 停车场地为${parkingStructure}，共有停车位${parkingSpots}个，私家车位${privateSpotsText}`
        );
        // 第三条：道闸打通情况，字段为空不展示；是→已打通，否→未打通
        if (barrier === '是') {
          lines.push('- 地上地下停车道闸已打通');
        } else if (barrier === '否') {
          lines.push('- 地上地下停车道闸未打通');
        }
        // 第四条：地上/地下无桩电车总数及纯电/混动明细
        lines.push(
          `- 共有地上无桩电车${this.groundNoPileTotal}辆（纯电${groundPureEv}辆/混动${groundHybrid}辆），` +
            `地下无桩电车${this.undergroundNoPileTotal}辆（纯电${undergroundPureEv}辆/混动${undergroundHybrid}辆）`
        );
        // 第五条：私桩和竞品数量始终包含（为0也显示）
        lines.push(`- 已安装私桩${privatePile}台，竞品充电桩${competitorPileCount}台`);
        // 末条：固定文案
        lines.push('- 可继续补充填写规划投建车位位置、场地接电方案等其他项目重点事项');
        f.summary = lines.join('\n');
        this.$message.success('生成总结成功');
      },
      addCompetitor() {
        this.form.competitors.push(defaultCompetitor());
      },
      removeCompetitor(index) {
        this.form.competitors.splice(index, 1);
      },
      openPhotoUploader(key) {
        this.currentUploadKey = key;
        this.$refs.fileInput.click();
      },
      // 获取文件指纹（文件名 + 文件大小）
      getFileIdentity(file) {
        return `${file.name}_${file.size}`;
      },
      // 从后端获取当前核场记录下已有照片名称（用于去重）
      fetchServerPhotoNames() {
        if (!this.form.id) {
          serverPhotoNames = new Set();
          return Promise.resolve();
        }
        return inspectionRecordGetPhotoNames({ recordId: this.form.id })
          .then(names => {
            serverPhotoNames = new Set(Array.isArray(names) ? names : []);
          })
          .catch(err => {
            console.error('获取已有照片名称失败:', err);
          });
      },
      // 获取文件扩展名
      getFileExt(file) {
        const name = file.name || '';
        const idx = name.lastIndexOf('.');
        return idx > -1 ? name.substring(idx) : '.jpg';
      },
      // 构建 OSS 对象 Key
      buildOssObjectKey(policy, file) {
        const ext = this.getFileExt(file);
        const random = Math.random().toString(36).slice(2, 10);
        return `${policy.dir}${Date.now()}_${random}${ext}`;
      },
      // 客户端压缩图片（上传前调用，减少传输体积）
      compressImage(file, maxWidth = 1920, quality = 0.7) {
        // 小于 500KB 不压缩
        if (file.size < 500 * 1024) return Promise.resolve(file);
        return new Promise(resolve => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            URL.revokeObjectURL(url);
            // 计算缩放尺寸
            let { width, height } = img;
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              blob => {
                if (blob && blob.size < file.size) {
                  resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                } else {
                  resolve(file); // 压缩后反而更大，用原图
                }
              },
              'image/jpeg',
              quality
            );
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
          };
          img.src = url;
        });
      },
      // 获取 OSS 上传策略（缓存复用）
      getOssPolicy(bizName = 'projectVerification') {
        const now = Date.now();
        // 缓存 50 秒（STS 令牌通常 60 秒过期）
        if (this.ossPolicyCache && now < this.ossPolicyExpireTime) {
          return Promise.resolve(this.ossPolicyCache);
        }
        return inspectionRecordOssDirectUploadPolicy({ bizName })
          .then(policyRes => {
            console.log('[OSS Policy] 后端返回:', JSON.stringify(policyRes));
            this.ossPolicyCache = policyRes || {};
            this.ossPolicyExpireTime = now + 50 * 1000;
            return this.ossPolicyCache;
          })
          .catch(err => {
            console.error('[OSS Policy] 获取失败:', err);
            throw err;
          });
      },
      // 前端直传单个文件到 OSS（支持传入已获取的 policy）
      uploadFileToOss(file, cachedPolicy, bizName = 'projectVerification') {
        const policyPromise = cachedPolicy
          ? Promise.resolve(cachedPolicy)
          : this.getOssPolicy(bizName);
        return policyPromise.then(policy => {
          const key = this.buildOssObjectKey(policy, file);
          console.log('[OSS Upload] policy详情:', {
            host: policy.host,
            accessId: policy.accessId,
            accessId类型: typeof policy.accessId,
            accessId长度: policy.accessId ? policy.accessId.length : 0,
            signature: policy.signature ? `${policy.signature.substring(0, 10)}...` : 'EMPTY',
            policy字段: policy.policy ? `${policy.policy.substring(0, 10)}...` : 'EMPTY',
            dir: policy.dir,
            securityToken: policy.securityToken ? 'HAS_TOKEN' : 'NO_TOKEN',
          });
          const formData = new FormData();
          formData.append('key', key);
          formData.append('policy', policy.policy);
          formData.append('OSSAccessKeyId', policy.accessId);
          formData.append('signature', policy.signature);
          formData.append('success_action_status', '200');
          if (policy.securityToken) {
            formData.append('x-oss-security-token', policy.securityToken);
          }
          formData.append('file', file);
          return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            console.log('[OSS Upload] 开始上传, host:', policy.host, 'key:', key);
            xhr.open('POST', policy.host, true);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                // projectVerification 仅返回 objectKey（与小程序一致，后端负责生成可访问 URL）
                if (bizName === 'projectVerification') {
                  resolve(key);
                } else {
                  const prefix = policy.urlPrefix || policy.host;
                  resolve(`${prefix.replace(/\/$/, '')}/${key}`);
                }
              } else {
                console.error('[OSS Upload] 失败:', xhr.status, xhr.responseText);
                reject(new Error(`OSS上传失败：${xhr.status}`));
              }
            };
            xhr.onerror = () => {
              console.error('[OSS Upload] 网络错误, host:', policy.host);
              reject(new Error('OSS上传网络错误'));
            };
            xhr.send(formData);
          });
        });
      },
      async handleFileChange(e) {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const key = this.currentUploadKey;
        this.$refs.fileInput.value = '';
        if (!this.form.id) {
          this.$message.warning('请先保存草稿后再上传图片');
          return;
        }
        const loading = this.$loading({ lock: true, text: '上传中...' });
        try {
          // 获取服务端已有照片名称用于去重
          await this.fetchServerPhotoNames();
          // 去重：本地 identity + 服务端文件名
          const categorySet = uploadedPhotoIdentitiesByCategory[key] || new Set();
          const uniqueFiles = [];
          let duplicatedCount = 0;
          const batchSet = new Set();
          Array.from(files).forEach(file => {
            const identity = this.getFileIdentity(file);
            const fileName = file.name || '';
            if (categorySet.has(identity) || batchSet.has(identity)) {
              duplicatedCount++;
            } else if (fileName && serverPhotoNames.has(fileName)) {
              duplicatedCount++;
            } else {
              batchSet.add(identity);
              uniqueFiles.push(file);
            }
          });
          if (duplicatedCount > 0 && !uniqueFiles.length) {
            // 全部重复
            this.$message.info(`共选择 ${files.length} 张，${duplicatedCount} 张重复已忽略`);
            return;
          }
          if (!uniqueFiles.length) return;
          const ossPolicy = await this.getOssPolicy();
          const photoCategory = PHOTO_CATEGORY_MAP[key] || undefined;
          const results = await Promise.all(
            uniqueFiles.map(file =>
              this.uploadFileToOss(file, ossPolicy, 'projectVerification')
                .then(url => {
                  const identity = this.getFileIdentity(file);
                  if (!uploadedPhotoIdentitiesByCategory[key]) {
                    uploadedPhotoIdentitiesByCategory[key] = new Set();
                  }
                  uploadedPhotoIdentitiesByCategory[key].add(identity);
                  uploadedPhotoIdentities.add(identity);
                  return { url, photoName: file.name, identity };
                })
                .catch(err => {
                  console.error('图片上传失败:', err);
                  return '';
                })
            )
          );
          const validResults = results.filter(item => item && item.url);
          const failedCount = uniqueFiles.length - validResults.length;
          // 先用本地 blob URL 即时显示图片
          const localUrlMap = {};
          validResults.forEach(item => {
            const file = uniqueFiles.find(f => f.name === item.photoName);
            if (file) {
              localUrlMap[item.url] = URL.createObjectURL(file);
            }
            if (!Array.isArray(this.form[key])) {
              this.$set(this.form, key, []);
            }
            // 页面展示用 blob URL（兜底服务端URL），并记录展示URL -> 照片元信息映射，
            // 供点叉删除时清理去重集合并同步软删服务端照片记录
            const displayUrl = localUrlMap[item.url] || item.url;
            this.form[key].push(displayUrl);
            const meta = {
              ossKey: item.url,
              photoName: item.photoName,
              identity: item.identity,
            };
            photoMetaByUrl[displayUrl] = meta;
            // 保存成功后详情重载会把展示 URL 替换为服务端签名 URL，届时点叉只能
            // 通过签名 URL 还原 ossKey 查映射，需同时以 ossKey 为键存一份保证命中，
            // 否则 identity 残留导致重传同一张照片被本地去重误拦截
            if (displayUrl !== item.url) {
              photoMetaByUrl[item.url] = meta;
            }
          });
          // 汇总提示
          const parts = [`已上传 ${validResults.length} 张图片`];
          if (duplicatedCount > 0) parts.push(`${duplicatedCount} 张重复已忽略`);
          if (failedCount > 0) parts.push(`${failedCount} 张上传失败`);
          this.$message.success(parts.join('，'));
          // 保存到后端（如果已有记录 ID），传入分类类型
          if (this.form.id && validResults.length > 0) {
            // 无桩电车图片上传后触发 AI 分类
            const needClassify = key === 'evPhotos';
            this.captureClassifyBaseline()
              .then(() =>
                inspectionRecordSaveAndClassifyImages({
                  recordId: this.form.id,
                  imageConetentList: validResults.map(item => ({
                    url: item.url,
                    photoName: item.photoName,
                  })),
                  classify: needClassify,
                  photoType: photoCategory,
                })
              )
              .then(() => {
                if (needClassify) {
                  // 开始轮询 AI 分类进度
                  this.classifying = true;
                  this.classifyProgress = {
                    total: validResults.length,
                    classified: 0,
                    unclassified: validResults.length,
                  };
                  this.pollClassifyProgress();
                }
                // 保存成功后重新加载详情，用后端可访问的 URL 替换本地 blob URL
                return inspectionRecordDetail({ id: this.form.id });
              })
              .then(res => {
                if (res) {
                  this.distributeClassifiedPhotos(res);
                }
              })
              .catch(err => {
                console.error('手动上传保存后端失败:', err);
              });
          }
        } catch (err) {
          console.error('上传失败:', err);
          this.$message.error(`上传失败: ${err.message || '请重试'}`);
        } finally {
          loading.close();
        }
      },
      handleBatchUpload() {
        if (this.batchUploading) return;
        if (!this.form.id) {
          this.$message.warning('请先保存草稿后再批量上传图片');
          return;
        }
        this.$refs.batchFileInput.click();
      },
      async handleBatchFileChange(e) {
        let files = Array.from(e.target.files || []);
        this.$refs.batchFileInput.value = '';
        if (!files.length) return;

        // 最多选择 300 张
        const MAX_SELECT = 300;
        if (files.length > MAX_SELECT) {
          this.$message.warning(`最多选择 ${MAX_SELECT} 张照片，已截取前 ${MAX_SELECT} 张`);
          files = files.slice(0, MAX_SELECT);
        }

        // 立即显示上传中状态
        this.batchUploading = true;
        this.batchUploadTotal = files.length;
        this.batchUploadSuccess = 0;

        // 并行获取：服务端已有照片名称 + OSS上传策略 + 分类进度基线（节省一次串行等待）
        const [ossPolicy] = await Promise.all([
          this.getOssPolicy(),
          this.fetchServerPhotoNames(),
          this.captureClassifyBaseline(),
        ]);

        // 去重：过滤已上传的图片（本地指纹 + 服务端文件名）
        const uniqueFiles = [];
        let duplicatedCount = 0;
        const batchSet = new Set();
        files.forEach(file => {
          const identity = this.getFileIdentity(file);
          const fileName = file.name || '';
          if (uploadedPhotoIdentities.has(identity) || batchSet.has(identity)) {
            duplicatedCount++;
          } else if (fileName && serverPhotoNames.has(fileName)) {
            duplicatedCount++;
          } else {
            batchSet.add(identity);
            uniqueFiles.push(file);
          }
        });
        if (duplicatedCount > 0) {
          this.$message.info(`已过滤 ${duplicatedCount} 张重复图片`);
        }
        if (!uniqueFiles.length) {
          this.$message.warning('全部为重复图片，无需上传');
          this.batchUploading = false;
          return;
        }

        // 更新实际的去重后数量
        this.batchUploadTotal = uniqueFiles.length;

        // 优化：OSS 上传不间断，后端保存异步触发不阻塞下一批
        const BATCH_SIZE = 10;
        let totalSaved = 0;
        const batchCount = Math.ceil(uniqueFiles.length / BATCH_SIZE);
        try {
          await Array.from({ length: batchCount }, (_, i) => i).reduce(
            async (chain, batchIndex) => {
              await chain;
              const start = batchIndex * BATCH_SIZE;
              const batchFiles = uniqueFiles.slice(start, start + BATCH_SIZE);
              const batchResults = await Promise.all(
                batchFiles.map(file =>
                  // 先压缩再上传（减少传输体积）
                  this.compressImage(file).then(compressed =>
                    this.uploadFileToOss(compressed, ossPolicy, 'projectVerification')
                      .then(url => {
                        // 记录照片元信息映射，供点叉删除时清理去重集合并同步软删服务端照片记录
                        const identity = this.getFileIdentity(file);
                        uploadedPhotoIdentities.add(identity);
                        photoMetaByUrl[url] = { ossKey: url, photoName: file.name, identity };
                        return { url, photoName: file.name, identity };
                      })
                      .catch(err => {
                        console.error('图片上传失败:', err);
                        return null;
                      })
                  )
                )
              );
              const batchItems = batchResults.filter(item => item && item.url);
              if (batchItems.length > 0) {
                this.batchUploadSuccess += batchItems.length;
                // 同步调用后端保存（等待本批保存完成再继续下一批，避免后端 AI 分类过载）
                try {
                  await inspectionRecordSaveAndClassifyImages({
                    recordId: this.form.id,
                    imageConetentList: batchItems.map(item => ({
                      url: item.url,
                      photoName: item.photoName,
                    })),
                    classify: true,
                  });
                  totalSaved += batchItems.length;
                } catch (err) {
                  console.error('本批次保存分类失败:', err);
                }
              }
            },
            Promise.resolve()
          );

          if (totalSaved === 0) {
            this.$message.error('上传失败，请重试');
            return;
          }

          // 上传完成后立即重新加载详情，同步照片列表和 EV 数量
          inspectionRecordDetail({ id: this.form.id })
            .then(res => {
              if (res) this.distributeClassifiedPhotos(res);
            })
            .catch(() => {});

          // 开始轮询 AI 分类进度
          this.classifying = true;
          this.classifyProgress = { total: totalSaved, classified: 0, unclassified: totalSaved };
          this.pollClassifyProgress();
        } finally {
          this.batchUploading = false;
        }
      },
      // 上传/触发分类前记录整记录分类进度基线，轮询展示时按本次批次换算增量
      captureClassifyBaseline() {
        if (!this.form.id) {
          this.classifyProgressBaseline = { total: 0, classified: 0 };
          return Promise.resolve();
        }
        return inspectionRecordGetClassifyProgress({ recordId: this.form.id })
          .then(progress => {
            this.classifyProgressBaseline = {
              total: (progress && progress.total) || 0,
              classified: (progress && progress.classified) || 0,
            };
          })
          .catch(() => {
            this.classifyProgressBaseline = { total: 0, classified: 0 };
          });
      },
      pollClassifyProgress() {
        if (!this.form.id) return;
        const id = this.form.id;
        // 全局只保留一个轮询循环，防止多次上传叠加多个轮询
        if (this.classifyPollTimer) {
          clearTimeout(this.classifyPollTimer);
          this.classifyPollTimer = null;
        }
        let pollCount = 0;
        const maxPoll = 60; // 最多轮询 60 次，每次间隔 5 秒
        const doPoll = () => {
          pollCount++;
          inspectionRecordGetClassifyProgress({ recordId: id })
            .then(progress => {
              if (progress) {
                // 展示值换算为本次批次增量（基线在触发分类前记录），完成判断仍用整记录值
                this.classifyProgress = {
                  total: Math.max((progress.total || 0) - this.classifyProgressBaseline.total, 0),
                  classified: Math.max(
                    (progress.classified || 0) - this.classifyProgressBaseline.classified,
                    0
                  ),
                  unclassified: progress.unclassified || 0,
                };
                // 全部分类完成、后端无进行中分类任务或达到最大轮询次数时停止
                // AI返回"未分类"的图片会一直停留在UNCLASSIFIED，任务结束需依赖classifying标记判断
                if (
                  progress.unclassified === 0 ||
                  progress.classifying === false ||
                  pollCount >= maxPoll
                ) {
                  this.classifying = false;
                  this.classifyPollTimer = null;
                  if (progress.unclassified > 0 || progress.failed > 0) {
                    this.$message({
                      type: 'warning',
                      message: `AI分类完成，${
                        (progress.unclassified || 0) + (progress.failed || 0)
                      } 张待人工处理`,
                    });
                  } else {
                    // 与进度条一致：按本次上传批次展示张数，而非整记录总数
                    const batchTotal = Math.max(
                      (progress.total || 0) - this.classifyProgressBaseline.total,
                      0
                    );
                    this.$message.success(`AI分类完成，共 ${batchTotal} 张图片`);
                  }
                  inspectionRecordDetail({ id })
                    .then(res => {
                      if (res) this.distributeClassifiedPhotos(res);
                    })
                    .catch(() => {});
                  return;
                }
              }
              this.classifyPollTimer = setTimeout(doPoll, 5000);
            })
            .catch(() => {
              if (pollCount < maxPoll) {
                this.classifyPollTimer = setTimeout(doPoll, 5000);
              } else {
                this.classifying = false;
                this.classifyPollTimer = null;
              }
            });
        };
        this.classifyPollTimer = setTimeout(doPoll, 1000);
      },
      pollAiClassifyResult() {
        if (!this.form.id) return;
        const id = this.form.id;
        let pollCount = 0;
        const maxPoll = 4;
        const doPoll = () => {
          pollCount++;
          inspectionRecordDetail({ id })
            .then(res => {
              if (res) {
                this.distributeClassifiedPhotos(res);
              }
            })
            .catch(() => {});
          if (pollCount < maxPoll) {
            setTimeout(doPoll, 5000);
          }
        };
        setTimeout(doPoll, 5000);
      },
      distributeClassifiedPhotos(detail) {
        // 根据后端返回的分类结果重新分配图片到对应区域
        const categoryMap = {
          SCENE: 'scenePhotos',
          EV: 'evPhotos',
          PRIVATE_PILE: 'privatePilePhotos',
          COMPETE_PILE: 'competePilePhotos',
          COMPETE_SCREENSHOT: 'competeScreenshotPhotos',
        };
        // 更新表单中的图片列表
        Object.keys(categoryMap).forEach(cat => {
          const key = categoryMap[cat];
          const photos = detail[key] || [];
          this.$set(this.form, key, photos);
        });
        // 同步待分类分组（已分类的移出，分类失败的留在待分类）
        this.$set(this.form, 'unclassifiedPhotos', detail.unclassifiedPhotos || []);
        // 同步电车图位置信息
        if (detail.evPhotoLocations) {
          this.$set(this.form, 'evPhotoLocations', detail.evPhotoLocations);
        }
        if (detail.evPhotoCarTypes) {
          this.$set(this.form, 'evPhotoCarTypes', detail.evPhotoCarTypes);
        }
        if (detail.evPhotoBrands) {
          this.$set(this.form, 'evPhotoBrands', detail.evPhotoBrands);
        }
        if (detail.evPhotoModels) {
          this.$set(this.form, 'evPhotoModels', detail.evPhotoModels);
        }
        if (detail.evPhotoLicensePlates) {
          this.$set(this.form, 'evPhotoLicensePlates', detail.evPhotoLicensePlates);
        }
        // 车辆数量由人工手动填写，不再从图片统计同步
      },
      clearPhotoList(key) {
        this.$confirm(`确认清空该类型所有图片？`, '提示', {
          confirmButtonText: '确认清空',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => {
            const removedUrls = this.form[key] || [];
            this.form[key] = [];
            uploadedPhotoIdentities.clear();
            if (uploadedPhotoIdentitiesByCategory[key]) {
              uploadedPhotoIdentitiesByCategory[key].clear();
            }
            // 清理映射并同步软删服务端照片记录（与点叉删除一致），避免清空后重传被去重拦截
            const photoUrls = removedUrls
              .map(url => {
                const ossKeyOfDisplay = url ? normalizeOssKey(url) : '';
                const meta =
                  (url && photoMetaByUrl[url]) ||
                  (ossKeyOfDisplay && photoMetaByUrl[ossKeyOfDisplay]) ||
                  null;
                if (meta) {
                  if (url) delete photoMetaByUrl[url];
                  if (meta.ossKey) delete photoMetaByUrl[meta.ossKey];
                  return meta.ossKey;
                }
                return ossKeyOfDisplay;
              })
              .filter(Boolean);
            if (this.form.id && photoUrls.length) {
              inspectionRecordDeletePhotos({ recordId: this.form.id, photoUrls }).catch(() => {});
            }
            if (key === 'evPhotos') {
              this.form.evPhotoLocations = [];
              this.form.evPhotoCarTypes = [];
              this.form.evPhotoBrands = [];
              this.form.evPhotoModels = [];
              this.form.evPhotoLicensePlates = [];
            }
            this.$message.success('已清空');
          })
          .catch(() => {});
      },
      removePhoto(key, index) {
        const displayUrl = this.form[key] && this.form[key][index];
        this.form[key].splice(index, 1);
        if (key === 'evPhotos') {
          if (this.form.evPhotoLocations) this.form.evPhotoLocations.splice(index, 1);
          if (this.form.evPhotoCarTypes) this.form.evPhotoCarTypes.splice(index, 1);
          if (this.form.evPhotoBrands) this.form.evPhotoBrands.splice(index, 1);
          if (this.form.evPhotoModels) this.form.evPhotoModels.splice(index, 1);
          if (this.form.evPhotoLicensePlates) this.form.evPhotoLicensePlates.splice(index, 1);
        }
        // 清理本地去重集合并同步软删服务端照片记录，
        // 避免未提交前重新上传同一张照片被本地/服务端去重误拦截
        const ossKeyOfDisplay = displayUrl ? normalizeOssKey(displayUrl) : '';
        const meta =
          (displayUrl && photoMetaByUrl[displayUrl]) ||
          (ossKeyOfDisplay && photoMetaByUrl[ossKeyOfDisplay]) ||
          null;
        if (meta) {
          if (uploadedPhotoIdentitiesByCategory[key]) {
            uploadedPhotoIdentitiesByCategory[key].delete(meta.identity);
          }
          uploadedPhotoIdentities.delete(meta.identity);
          if (displayUrl) delete photoMetaByUrl[displayUrl];
          if (meta.ossKey) delete photoMetaByUrl[meta.ossKey];
        }
        const targetKey = meta ? meta.ossKey : ossKeyOfDisplay;
        if (this.form.id && targetKey) {
          inspectionRecordDeletePhotos({ recordId: this.form.id, photoUrls: [targetKey] }).catch(
            () => {}
          );
        }
      },
      handlePreviewPhoto(key, idx) {
        const list = this.form[key] || [];
        if (!list[idx]) return;
        this.previewSrc = list[idx];
        this.previewList = list;
        this.previewVisible = true;
      },
      getLocationText(idx) {
        const loc = this.form.evPhotoLocations && this.form.evPhotoLocations[idx];
        if (loc !== undefined && loc !== null) {
          if (loc === 'UNDERGROUND' || loc === '地下') return '地下';
          if (loc === 'GROUND' || loc === '地上') return '地上';
          return null;
        }
        return null;
      },
      // 获取电车图位置值：'地上'、'地下' 或 null（未选择）
      getEvLocationValue(idx) {
        const loc = this.form.evPhotoLocations && this.form.evPhotoLocations[idx];
        if (loc !== undefined && loc !== null) {
          if (loc === 'UNDERGROUND' || loc === '地下') return '地下';
          if (loc === 'GROUND' || loc === '地上') return '地上';
          return null;
        }
        // evPhotoLocations 未设置 -> 无位置（不回退 carType，避免错误默认值）
        return null;
      },
      // 设置电车图位置
      setEvLocation(idx, location) {
        if (!this.form.evPhotoLocations) this.form.evPhotoLocations = [];
        this.$set(this.form.evPhotoLocations, idx, location);
        if (!this.form.evPhotoCarTypes) this.form.evPhotoCarTypes = [];
        this.$set(this.form.evPhotoCarTypes, idx, location === '地下' ? 2 : 1);
        // 调用后台接口更新
        const recordId = this.form.id || this.$route.query.id;
        if (recordId) {
          const photoUrl = this.form.evPhotos[idx];
          inspectionRecordUpdateImageLocation({
            recordId: Number(recordId),
            imageConetentList: [{ url: photoUrl }],
            photoType: 'EV',
            photoLocation: location === '地下' ? 'UNDERGROUND' : 'GROUND',
            carType: location === '地下' ? 2 : 1,
          })
            .then(() => {
              this.$message.success(`已切换为${location}`);
            })
            .catch(err => {
              console.error('更新位置失败:', err);
              this.$message.error('更新位置失败');
            });
        }
      },
      toggleLocationTag(idx) {
        if (!this.form.evPhotoLocations) this.form.evPhotoLocations = [];
        const current = this.getLocationText(idx);
        const next = current === '地上' ? '地下' : '地上';
        this.$set(this.form.evPhotoLocations, idx, next);
        // 同步到 evPhotoCarTypes
        if (!this.form.evPhotoCarTypes) this.form.evPhotoCarTypes = [];
        this.$set(this.form.evPhotoCarTypes, idx, next === '地下' ? 2 : 1);
        // 调用后台接口更新位置
        const recordId = this.form.id || this.$route.query.id;
        if (recordId) {
          const photoUrl = this.form.evPhotos[idx];
          inspectionRecordUpdateImageLocation({
            recordId: Number(recordId),
            imageConetentList: [{ url: photoUrl }],
            photoType: 'EV',
            photoLocation: next === '地下' ? 'UNDERGROUND' : 'GROUND',
            carType: next === '地下' ? 2 : 1,
          }).catch(err => {
            console.error('更新位置失败:', err);
            this.$message.error('更新位置失败');
          });
        }
      },
      // 加载省市区数据
      loadDistrictData() {
        treeUtils
          .getDistrict({})
          .then(res => {
            console.log('[省市区] 接口返回:', JSON.stringify(res).substring(0, 500));
            const districts = res?.districts || [];
            console.log('[省市区] 省份数量:', districts.length);
            if (districts.length > 0) {
              const sample = districts[0];
              console.log(
                '[省市区] 示例省份:',
                sample.name,
                'subNodes:',
                sample.subNodes ? sample.subNodes.length : 'undefined'
              );
              if (sample.subNodes && sample.subNodes.length > 0) {
                const sampleCity = sample.subNodes[0];
                console.log(
                  '[省市区] 示例城市:',
                  sampleCity.name,
                  'subNodes:',
                  sampleCity.subNodes ? sampleCity.subNodes.length : 'undefined'
                );
              }
            }
            this.allDistrictData = districts;
            this.provinceList = districts.map(p => ({ id: p.id, name: p.name }));
          })
          .catch(err => {
            console.error('加载省市区数据失败:', err);
          });
      },
      // 仅填充城市/区县下拉列表，不清空表单值（用于数据回填）
      populateCityDistrictLists() {
        if (!this.form.province || !this.allDistrictData.length) return;
        const province = this.allDistrictData.find(p => p.name === this.form.province);
        if (province && province.subNodes) {
          this.cityList = province.subNodes.map(c => ({ id: c.id, name: c.name }));
          if (this.form.city) {
            const city = province.subNodes.find(c => c.name === this.form.city);
            if (city && city.subNodes) {
              this.districtList = city.subNodes.map(d => ({ id: d.id, name: d.name }));
            }
          }
        }
      },
      onProvinceChange(provinceName) {
        this.form.city = '';
        this.form.district = '';
        this.cityList = [];
        this.districtList = [];
        if (!provinceName) return;
        const province = this.allDistrictData.find(p => p.name === provinceName);
        if (province && province.subNodes) {
          this.cityList = province.subNodes.map(c => ({ id: c.id, name: c.name }));
        }
      },
      onCityChange(cityName) {
        this.form.district = '';
        this.districtList = [];
        if (!cityName || !this.form.province) return;
        const province = this.allDistrictData.find(p => p.name === this.form.province);
        console.log(
          '[省市区] 选择城市:',
          cityName,
          '找到省份:',
          province ? province.name : '未找到'
        );
        if (province && province.subNodes) {
          const city = province.subNodes.find(c => c.name === cityName);
          console.log(
            '[省市区] 找到城市:',
            city ? city.name : '未找到',
            'city.subNodes:',
            city && city.subNodes ? city.subNodes.length : 'undefined'
          );
          if (city && city.subNodes) {
            this.districtList = city.subNodes.map(d => ({ id: d.id, name: d.name }));
            console.log('[省市区] 区县数量:', this.districtList.length);
          }
        }
      },
      handlePhotoDragStart(category, idx, event) {
        console.log('[拖拽] dragstart:', category, idx);
        this.dragSource = { category, idx, url: this.form[category][idx] };
        console.log('[拖拽] dragSource:', JSON.stringify(this.dragSource));
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', ''); // 兼容 Firefox
        // 添加拖拽视觉反馈
        const el = event.target.closest('.photo-item');
        if (el) {
          this.$nextTick(() => el.classList.add('dragging'));
        }
      },
      handlePhotoDragEnd(event) {
        console.log(
          '[拖拽] dragend, dragSource:',
          this.dragSource ? JSON.stringify(this.dragSource) : 'null'
        );
        this.dragOverCategory = '';
        const el = event.target.closest('.photo-item');
        if (el) el.classList.remove('dragging');
        // 不在这里清空 dragSource，由 handlePhotoDrop 负责
      },
      handlePhotoDrop(targetCategory, event) {
        event.preventDefault();
        console.log(
          '[拖拽] drop:',
          targetCategory,
          'dragSource:',
          this.dragSource ? JSON.stringify(this.dragSource) : 'null'
        );
        this.dragOverCategory = '';
        if (!this.dragSource) {
          console.log('[拖拽] dragSource 为空，跳过');
          return;
        }
        const { category: sourceCategory, idx: sourceIdx, url } = this.dragSource;
        this.dragSource = null;
        // 同一分类内不处理
        if (sourceCategory === targetCategory) {
          console.log('[拖拽] 同一分类，跳过');
          return;
        }
        console.log(
          '[拖拽] 移动图片:',
          sourceCategory,
          '→',
          targetCategory,
          'url:',
          url ? url.substring(0, 50) : 'null'
        );
        // 从源分类移除
        this.form[sourceCategory].splice(sourceIdx, 1);
        // 保存源分类的 EV 关联数据（先取再删）
        let srcBrand;
        let srcModel;
        let srcPlate;
        if (sourceCategory === 'evPhotos') {
          if (this.form.evPhotoLocations) this.form.evPhotoLocations.splice(sourceIdx, 1);
          if (this.form.evPhotoCarTypes) this.form.evPhotoCarTypes.splice(sourceIdx, 1);
          if (this.form.evPhotoBrands) srcBrand = this.form.evPhotoBrands.splice(sourceIdx, 1)[0];
          if (this.form.evPhotoModels) srcModel = this.form.evPhotoModels.splice(sourceIdx, 1)[0];
          if (this.form.evPhotoLicensePlates)
            srcPlate = this.form.evPhotoLicensePlates.splice(sourceIdx, 1)[0];
        }
        // 添加到目标分类
        this.form[targetCategory].push(url);
        // 电车图需要初始化位置信息
        if (targetCategory === 'evPhotos') {
          if (!this.form.evPhotoLocations) this.form.evPhotoLocations = [];
          this.form.evPhotoLocations.push('');
          if (!this.form.evPhotoCarTypes) this.form.evPhotoCarTypes = [];
          this.form.evPhotoCarTypes.push(null);
          if (!this.form.evPhotoBrands) this.form.evPhotoBrands = [];
          this.form.evPhotoBrands.push(srcBrand || '');
          if (!this.form.evPhotoModels) this.form.evPhotoModels = [];
          this.form.evPhotoModels.push(srcModel || '');
          if (!this.form.evPhotoLicensePlates) this.form.evPhotoLicensePlates = [];
          this.form.evPhotoLicensePlates.push(srcPlate || '');
        }
        this.$message.success('已移动分类');
        // 调用后台接口更新分类
        const doUpdateCategory = () => {
          inspectionRecordUpdateImageCategory({
            recordId: this.form.id,
            imageConetentList: [{ url }],
            photoType: PHOTO_CATEGORY_MAP[targetCategory],
          })
            .then(() => {
              // 拖拽到无桩电车分类时，触发 AI 分类
              if (targetCategory === 'evPhotos') {
                return this.captureClassifyBaseline().then(() =>
                  inspectionRecordSaveAndClassifyImages({
                    recordId: this.form.id,
                    imageConetentList: [],
                    classify: true,
                  }).then(() => {
                    this.classifying = true;
                    this.classifyProgress = { total: 1, classified: 0, unclassified: 1 };
                    this.pollClassifyProgress();
                  })
                );
              }
              return Promise.resolve();
            })
            .catch(err => {
              console.error('更新分类失败:', err);
              this.$message.error('更新分类失败');
            });
        };
        if (this.form.id) {
          doUpdateCategory();
        } else {
          // 先自动保存草稿获取 ID
          this.saving = true;
          inspectionRecordSave(this.form)
            .then(res => {
              if (res) {
                this.form.id = res;
                this.$router.replace({ name: 'inspectionRecordEdit', query: { id: res } });
                doUpdateCategory();
              }
            })
            .catch(err => {
              console.error('自动保存失败:', err);
              this.$message.error('请先保存草稿后再拖拽分类');
            })
            .finally(() => {
              this.saving = false;
            });
        }
      },
      loadDetail() {
        this.loading = true;
        // 重置模块级去重变量，防止上一条记录的缓存导致误判重复
        uploadedPhotoIdentities.clear();
        Object.keys(uploadedPhotoIdentitiesByCategory).forEach(
          k => delete uploadedPhotoIdentitiesByCategory[k]
        );
        serverPhotoNames = new Set();
        Object.keys(photoMetaByUrl).forEach(k => delete photoMetaByUrl[k]);
        inspectionRecordDetail({ id: this.$route.query.id })
          .then(res => {
            this.detail = res || {};
            const form = defaultForm();
            this.form = Object.assign(form, res || {});
            // 周边200米快充站枚举为有/无，兼容历史数据（是/否及编码1/0）
            const fastChargeMap = { 是: '有', 否: '无', 1: '有', 0: '无' };
            if (this.form.hasNearbyFastCharge in fastChargeMap) {
              this.form.hasNearbyFastCharge = fastChargeMap[this.form.hasNearbyFastCharge];
            }
            // 确保 Photo 字段为数组，防止后端返回 null 导致无法 push
            [
              'scenePhotos',
              'evPhotos',
              'privatePilePhotos',
              'competePilePhotos',
              'competeScreenshotPhotos',
              'unclassifiedPhotos',
            ].forEach(k => {
              if (!Array.isArray(this.form[k])) {
                this.form[k] = [];
              }
            });
            // surveyTime 字符串转 Date 对象供 el-date-picker 使用
            if (this.form.surveyTime && typeof this.form.surveyTime === 'string') {
              this.form.surveyTime = new Date(this.form.surveyTime.replace(/-/g, '/'));
            }
            // 回填省市区下拉列表（不清空已填值）
            this.$nextTick(() => this.populateCityDistrictLists());
            // 检查是否有未分类的照片，如果有则开始轮询
            this.checkClassifyProgress();
          })
          .catch(err => {
            console.error('加载详情失败:', err);
            this.$message.error('加载详情失败');
          })
          .finally(() => {
            this.loading = false;
          });
      },
      checkClassifyProgress() {
        if (!this.form.id) return;
        inspectionRecordGetClassifyProgress({ recordId: this.form.id })
          .then(progress => {
            if (!progress) return;
            this.classifyProgress = {
              total: progress.total || 0,
              classified: progress.classified || 0,
              unclassified: progress.unclassified || 0,
            };
            // 后端仍有进行中的分类任务时恢复轮询，刷新后继续回显进度
            // （基线保持{0,0}，此时按整记录总数展示）
            if (progress.classifying !== false && progress.unclassified > 0) {
              this.classifying = true;
              this.pollClassifyProgress();
            }
          })
          .catch(() => {});
      },
      buildSubmitData() {
        const data = { ...this.form };
        // 无桩新能源车 = 混动车 + 纯电车（自动计算，不可填写）
        data.aboveGroundNoPileEvCount = this.groundNoPileTotal;
        data.undergroundNoPileEvCount = this.undergroundNoPileTotal;
        // surveyTime Date 对象转字符串供后端使用
        if (data.surveyTime instanceof Date) {
          const d = data.surveyTime;
          const pad = n => String(n).padStart(2, '0');
          data.surveyTime = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
            d.getHours()
          )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
        // 过滤空值
        Object.keys(data).forEach(k => {
          if (data[k] === null || data[k] === undefined || data[k] === '') {
            // 保留空数组
            if (!Array.isArray(data[k])) {
              delete data[k];
            }
          }
        });
        return data;
      },
      handleSaveDraft() {
        this.saving = true;
        const data = this.buildSubmitData();
        inspectionRecordSave(data)
          .then(res => {
            this.$message.success('草稿保存成功');
            // 返回新 ID 后跳转为编辑模式
            if (res && !this.form.id) {
              this.form.id = res;
              this.$router.replace({ name: 'inspectionRecordEdit', query: { id: res } });
              this.detail.recordCode = this.detail.recordCode || '';
            }
          })
          .catch(err => {
            console.error('保存草稿失败:', err);
            this.$message.error('保存草稿失败');
          })
          .finally(() => {
            this.saving = false;
          });
      },
      handleSubmit() {
        // 提交前基本校验 - 基础信息模块（除备注外全部必填）
        const basicRequired = [
          { key: 'projectName', msg: '请填写项目名称' },
          { key: 'stationName', msg: '请填写站点名称' },
          { key: 'siteNature', msg: '请填写站点性质' },
          { key: 'surveyTime', msg: '请选择核场时间' },
          { key: 'province', msg: '请选择所在省份' },
          { key: 'city', msg: '请选择所在城市' },
          { key: 'district', msg: '请选择区/县' },
          { key: 'detailAddress', msg: '请填写项目地址' },
          { key: 'constructionTime', msg: '请填写项目建设时间' },
          { key: 'parkingSpots', msg: '请填写停车位数量', isNum: true },
          { key: 'parkingUsage', msg: '请填写车位使用情况' },
          { key: 'parkingSignal', msg: '请选择停车场信号' },
          { key: 'parkingStructure', msg: '请选择停车场结构' },
          { key: 'hasNearbyFastCharge', msg: '请选择周边200米快充站' },
          { key: 'privateParkingRatio', msg: '请填写私家车位比例' },
          { key: 'electricityUsageType', msg: '请选择我司用电性质' },
        ];
        // 地上/地下车辆数量仅在对应模块展示时必填（与停车场结构联动）
        if (this.form.parkingStructure !== '地下') {
          basicRequired.push(
            { key: 'aboveGroundHybridEvCount', msg: '请填写地上混动车数量', isNum: true },
            { key: 'aboveGroundPureEvCount', msg: '请填写地上纯电车数量', isNum: true }
          );
        }
        if (this.form.parkingStructure !== '地上') {
          basicRequired.push(
            { key: 'undergroundHybridEvCount', msg: '请填写地下混动车数量', isNum: true },
            { key: 'undergroundPureEvCount', msg: '请填写地下纯电车数量', isNum: true }
          );
        }
        const missingField = basicRequired.find(field => {
          const val = this.form[field.key];
          return field.isNum ? val === null || val === undefined || val === '' : !val;
        });
        if (missingField) {
          this.$message.warning(missingField.msg);
          this.expandedSections.basic = true;
          return;
        }
        if (this.form.parkingStructure === '地上+地下' && !this.form.barrierConnected) {
          this.$message.warning('请选择地上+地下道闸是否打通');
          this.expandedSections.basic = true;
          return;
        }
        if (this.form.suggestedPileCount === null) {
          this.$message.warning('请填写建议安装数量');
          this.expandedSections.summary = true;
          return;
        }
        if (!this.form.suggestedInstallLocation) {
          this.$message.warning('请填写建议安装位置');
          this.expandedSections.summary = true;
          return;
        }
        if (!this.form.summary) {
          this.$message.warning('请填写项目核场总结');
          this.expandedSections.summary = true;
          return;
        }

        this.submitting = true;
        const data = this.buildSubmitData();
        data.status = 'SUBMITTED';
        inspectionRecordSubmit(data)
          .then(() => {
            this.$message.success('提交成功');
            this.$router.push({ name: 'inspectionRecordList' });
          })
          .catch(err => {
            console.error('提交失败:', err);
            this.$message.error('提交失败');
          })
          .finally(() => {
            this.submitting = false;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .inspection-record-edit {
    background: #fafafa;
    min-height: 100vh;
  }

  /* ==================== 顶部 ==================== */
  .page-header {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .back-btn {
    font-size: 14px;
    color: #64748b;
    padding: 0;

    &:hover {
      color: #c9915e;
    }
  }

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .record-code-tag {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #475569;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  /* ==================== 主体 ==================== */
  .edit-body {
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* AI生成内容提示 */
  .ai-notice {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 14px;
    line-height: 1.6;
  }

  /* ==================== 卡片 ==================== */
  .section-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e4df;
    overflow: visible;
    transition: all 0.3s ease;
  }

  .section-card:hover {
    box-shadow: 0 4px 20px rgba(201, 145, 94, 0.08);
  }

  .section-header {
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: background 0.2s ease;
    user-select: none;
    border-radius: 12px 12px 0 0;
  }

  .section-header:hover {
    background: #fafafa;
  }

  .section-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }

  .section-subtitle {
    font-size: 12px;
    color: #94a3b8;
    margin: 2px 0 0;
  }

  .photo-spec-link {
    color: #c9915e;
    cursor: pointer;
  }

  .photo-spec-link:hover {
    opacity: 0.8;
  }

  .photo-spec-body {
    max-height: 65vh;
    overflow-y: auto;
  }

  .photo-spec-body .spec-block-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 16px 0 6px;
  }

  .photo-spec-body .spec-sub-title {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    margin: 12px 0 4px;
  }

  .photo-spec-body .spec-paragraph,
  .photo-spec-body .spec-li {
    font-size: 13px;
    color: #64748b;
    line-height: 1.7;
    margin: 0 0 4px;
  }

  .photo-spec-body .spec-li-sub {
    margin-left: 16px;
  }

  .section-arrow {
    color: #94a3b8;
    font-size: 14px;
    transition: transform 0.3s ease;
  }

  .section-arrow.rotated {
    transform: rotate(-90deg);
  }

  .section-content {
    padding: 0 20px 20px;
  }

  /* ==================== 表单 ==================== */
  .form-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .form-row.form-row-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-row.form-row-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  .form-item-full {
    grid-column: 1 / -1;
  }

  .form-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .form-label {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a2e;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
  }

  .form-label-tip {
    color: #94a3b8;
    font-weight: 400;
    font-size: 12px;
  }

  .required {
    color: #ef4444;
  }

  .ai-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 6px;
    background: linear-gradient(135deg, #fdf4e8 0%, #faf6f0 100%);
    border-radius: 4px;
    font-size: 10px;
    color: #c9915e;
    font-weight: 500;

    i {
      font-size: 9px;
    }
  }

  .stat-text {
    display: inline-block;
    padding: 4px 10px;
    font-size: 13px;
    color: #606266;
    background: #f5f7fa;
    border-radius: 4px;
    min-height: 28px;
    line-height: 20px;
  }

  .ai-btn {
    padding: 4px 10px;
    background: linear-gradient(135deg, #fdf4e8 0%, #faf6f0 100%);
    border-radius: 6px;
    font-size: 12px;
    color: #c9915e;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgba(201, 145, 94, 0.2);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(135deg, #faf0db 0%, #f5e9d4 100%);
    }

    i {
      font-size: 11px;
    }
  }

  /* ==================== 子分组（地上/地下车辆） ==================== */
  .sub-group {
    background: #fafafa;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 12px;
  }

  .sub-group-title {
    font-size: 12px;
    font-weight: 600;
    color: #c9915e;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 11px;
    }
  }

  /* ==================== 图片 ==================== */
  .batch-upload-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    background: linear-gradient(135deg, #c9915e 0%, #a67642 100%);
    border-radius: 10px;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px rgba(201, 145, 94, 0.25);
    margin-bottom: 16px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(201, 145, 94, 0.35);
    }

    &.disabled {
      opacity: 0.7;
      cursor: not-allowed;
      pointer-events: none;
    }

    i {
      font-size: 14px;
    }
  }

  .photo-block {
    margin-bottom: 16px;
  }

  .photo-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .photo-block-header .form-label {
    margin-bottom: 0;
  }

  .photo-count-tag {
    display: inline-block;
    padding: 1px 8px;
    background: #f0ebe4;
    color: #a67642;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
    margin-left: 6px;
  }

  .pending-classify-tip {
    font-size: 12px;
    color: #b8a88f;
  }

  .clear-btn {
    padding: 4px 8px;
    background: #fef2f2;
    border-radius: 4px;
    font-size: 11px;
    color: #ef4444;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #fee2e2;
    }
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
  }

  .photo-add {
    aspect-ratio: 1;
    border: 2px dashed #e8e4df;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fafafa;

    &:hover {
      border-color: #c9915e;
      background: #fdf4e8;
    }

    i {
      font-size: 18px;
      color: #94a3b8;
    }

    span {
      font-size: 10px;
      color: #94a3b8;
    }
  }

  .photo-item {
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    background: #f5f2ed;

    &[draggable='true'] {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    &.dragging {
      opacity: 0.4;
    }
  }

  .photo-more {
    min-height: 96px;
    border-radius: 8px;
    background: #f5f2ed;
    color: #94a3b8;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s;
    grid-column: span 1;

    i {
      font-size: 20px;
      margin-bottom: 4px;
    }

    &:hover {
      background: #ebe7e0;
      color: #64748b;
    }
  }

  .photo-grid {
    &.drag-over {
      outline: 2px dashed #c9915e;
      outline-offset: -2px;
      border-radius: 8px;
      background: rgba(201, 145, 94, 0.05);
    }
  }

  .photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    ::v-deep img {
      pointer-events: none;
      user-select: none;
      -webkit-user-drag: none;
    }
  }

  .photo-preview-dialog {
    .el-dialog__body {
      padding: 10px;
      text-align: center;
    }
  }

  .photo-delete {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 20px;
    height: 20px;
    background: rgba(239, 68, 68, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 5;

    i {
      font-size: 10px;
      color: #fff;
    }
  }

  .photo-location-tags {
    position: absolute;
    bottom: 4px;
    left: 4px;
    display: flex;
    gap: 4px;
    z-index: 5;
  }

  .loc-tag {
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    line-height: 1.4;

    &.active {
      color: #fff;
    }

    &.ground-tag.active {
      background: linear-gradient(135deg, #c9915e 0%, #a67642 100%);
      box-shadow: 0 2px 6px rgba(201, 145, 94, 0.3);
    }

    &.underground-tag.active {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
    }

    &.inactive {
      background: rgba(128, 128, 128, 0.8);
      color: rgba(255, 255, 255, 0.5);
    }

    &:active {
      transform: scale(0.92);
    }
  }

  .ev-info-tags {
    position: absolute;
    top: 4px;
    left: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    z-index: 5;
    max-width: calc(100% - 8px);
  }

  .ev-info-tag {
    padding: 1px 5px;
    font-size: 9px;
    font-weight: 500;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    line-height: 1.4;
    white-space: nowrap;
    backdrop-filter: blur(4px);
  }

  .photo-tip {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    padding: 6px 10px;
    background: #f0f7ff;
    border-radius: 6px;

    i {
      color: #3b82f6;
      font-size: 11px;
    }

    span {
      font-size: 11px;
      color: #64748b;
    }
  }

  .upload-tip-box {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #fdf4e8;
    border-radius: 8px;
    margin-top: 8px;

    i {
      color: #c9915e;
      font-size: 12px;
    }

    span {
      font-size: 12px;
      color: #64748b;
    }
  }

  /* ==================== 竞品 ==================== */
  .competitor-card {
    background: #fafafa;
    border-radius: 10px;
    padding: 14px;
    border: 1px solid #e8e4df;
    margin-bottom: 10px;
  }

  .competitor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .competitor-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .competitor-remove {
    color: #ef4444;
    font-size: 12px;
  }

  .add-competitor-btn {
    width: 100%;
    margin-top: 8px;
    border: 1px dashed #c9915e;
    background: #fdf4e8;
    color: #c9915e;

    &:hover {
      background: #faecdb;
    }
  }

  /* ==================== 提示框 ==================== */
  .tip-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fdf4e8;
    border-radius: 12px;
    padding: 12px 16px;
    margin-top: 4px;
  }

  .tip-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #c9915e;
    font-size: 14px;
  }

  .tip-title {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a2e;
    margin: 0 0 4px;
  }

  .tip-text {
    font-size: 12px;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
  }

  /* ==================== 弹窗 ==================== */
  .confirm-content {
    text-align: center;
    padding: 8px 0;
  }

  .confirm-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
    font-size: 24px;
  }

  .danger-icon {
    background: #fee2e2;
    color: #ef4444;
  }

  .confirm-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0 0 6px;
  }

  .confirm-desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
  }
</style>
